<template>
	<LayoutBase>
		<template v-if="!$wait.is('fetching preview resource')">

			<section class="preview-section">
				<template v-if="completedStep(1)">
					<header class="preview-section__header">
						<h2 class="h1">The Project</h2>
						<router-link :to="stepLink(1)" class="btn" v-if="canCompleteStep(1)">Edit</router-link>
					</header>
					<div class="company-info">
						<img
                            :src="resource.member.country.flag"
                            v-if="resource.member.country"
                            v-tippy="resource.member.country.name"
                        />
						<h2>{{ resource.projectName }}</h2>
					</div>
					<div v-if="resource.lead" class="profile-list">
						<p class="profile-list__title">Lead User</p>

						<UserCard :user="resource.lead" :borderless="true"></UserCard>
					</div>
					<div v-if="filterLead(resource.team).length" class="profile-list">
						<p class="profile-list__title">Users also involved</p>

						<div class="grid">
							<div class="col-3" v-for="profile in filterLead(resource.team)" :key="profile.id">
								<UserCard :user="profile" :small="true" :borderless="true"></UserCard>
							</div>
						</div>
					</div>
				</template>

				<p v-else>Not completed</p>
			</section>

			<section class="preview-section">
				<header class="preview-section__header">
					<h2 class="h1">The Company</h2>
					<router-link :to="stepLink(2)" class="btn" v-if="canCompleteStep(2)">Edit</router-link>
				</header>

				<template v-if="completedStep(2)">
					<div class="company-info">
						<h2>{{ resource.projectName }}</h2>
						<ul class="grid post-details">
							<li v-if="resource.createdAt" class="col-3 detail">
								<span class="label">Posted</span>
								<span class="h3"><SvgIcon :icon="'calendar'" /> {{ resource.createdAt }}</span>
							</li>
							<li v-if="resource.website" class="col-3 detail">
								<span class="label">Website</span>
								<a :href="resource.website" target="_blank" class="h3"> <SvgIcon :icon="'link'" /> Click here</a>
							</li>
							<li v-if="resource.location" class="col-6 detail">
								<span class="label">Location of Activities</span>
								<span class="h3"><SvgIcon :icon="'location'" /> {{ resource.location }}</span>
							</li>
						</ul>
					</div>
					<div class="grid">
						<div class="col-12 col-grid">
							<div class="card">
								<div class="header">Company Bio</div>
								<div v-html="resource.description"></div>
							</div>
						</div>
						<div class="col-8 col-grid">
							<div class="card">
								<div class="header">Context</div>
								<div v-html="resource.context"></div>
							</div>
						</div>
						<div class="col-4 col-grid">
							<div class="card">
								<div class="header">Buyer Examples</div>
								<div v-html="resource.buyerExamples"></div>
							</div>
						</div>
					</div>
				</template>

				<p v-else>Not completed</p>
			</section>

			<section class="preview-section">
				<header class="preview-section__header">
					<h2 class="h1">The Opportunity</h2>
					<router-link :to="stepLink(3)" class="btn" v-if="canCompleteStep(3)">Edit</router-link>
				</header>

				<template v-if="completedStep(3)">
					<ul class="grid post-details">
						<li v-if="resource.mandated" class="col-3 detail">
							<span class="label">Opportunity Type</span>
							<span class="h3"><SvgIcon :icon="'lock'" /> {{ resource.mandated | formatMandated }}</span>
						</li>
						<li v-if="resource.dealType" class="col-3 detail">
							<span class="label">Deal Type</span>
							<span class="h3"><SvgIcon :icon="'deal'" /> {{ resource.dealType }}</span>
						</li>
						<li v-if="resource.urgent !== null" class="col-3 detail">
							<span class="label">Priority</span>
							<span class="h3"><SvgIcon :icon="'priority'" /> {{ resource.urgent | formatUrgency }}</span>
						</li>
						<li v-if="resource.respond_by" class="col-3 detail">
							<span class="label">Response Details</span>
							<span class="h3"><SvgIcon :icon="'calendar'" /> {{ resource.respond_by }}</span>
						</li>
						<li v-if="resource.sectors" class="col-12 detail">
							<span class="label">Sectors</span>
							<span class="sector" v-for="sector in resource.sectors">{{ sector.name }}</span>
						</li>
					</ul>
				</template>

				<p v-else>Not completed</p>
			</section>

			<section class="preview-section">
				<header class="preview-section__header">
					<h2 class="h1">Values</h2>
					<router-link :to="stepLink(4)" class="btn" v-if="canCompleteStep(4)">Edit</router-link>
				</header>

				<template v-if="completedStep(4)">
					<ul class="grid post-details">
						<li v-if="resource.currency" class="col-2 detail">
							<span class="label">Currency</span>
							<strong><SvgIcon :icon="'money'" /> {{ resource.currency }}</strong>
						</li>
						<li v-if="resource.companyRevenue" class="col-2 detail">
							<span class="label">Revenue</span>
							<strong><SvgIcon :icon="'value'" /> {{ resource.companyRevenue }}m</strong>
						</li>
						<li v-if="resource.projectedValueFrom && resource.projectedValueTo" class="col-3 detail">
							<span class="label">Projected transaction value</span>
							<strong><SvgIcon :icon="'value'" /> {{ resource.projectedValueFrom }}m-{{ resource.projectedValueTo }}m</strong>
						</li>
					</ul>
				</template>

				<p v-else>Not completed</p>
			</section>

			<section class="preview-section">
				<header class="preview-section__header">
					<h2 class="h1">Supporting Documents</h2>
					<router-link :to="stepLink(5)" class="btn" v-if="canCompleteStep(5)">Edit</router-link>
				</header>

				<template v-if="completedStep(5)">
					<div class="card" v-if="resource.documents">
						<span class="header">Attachments</span>
						<ul class="unstyled">
							<li v-for="doc in resource.documents">
                                <AttachmentDownload :attachment="doc"></AttachmentDownload>
							</li>
						</ul>
					</div>
				</template>

				<p v-else>Not completed</p>
			</section>

			<div class="form">
				<div class="form__group form__group--submit-fixed">
					<button @click="showDeleteModal()" class="btn btn--danger" v-if="resource.can.delete"
					        v-wait:disabled="['saving preview resource', 'deleting preview resource']">
						Delete Opportunity
					</button>
					<router-link :to="{ name: 'opportunities', params: { tab: 'mine' }}"
					             class="btn" v-wait:disabled="'saving preview resource'">
						Save and Leave
					</router-link>
					<router-link :to="{ name: 'share-opportunity', params: { opportunity_id: this.id }}"
					             class="btn btn--primary btn--no-stretch" v-wait:disabled="'saving preview resource'"
					             v-if="resource.can.submit">
						Submit Opportunity
					</router-link>
				</div>
			</div>
		</template>
	</LayoutBase>
</template>

<script>
	import LayoutBase from '../Layout/LayoutBase';
	import API from '../../api';
	import SvgIcon from '../SvgIcon';
	import ResourcePreview from '../ResourcePreview';
	import UserCard from '../User/UserCard';
    import AttachmentDownload from '../AttachmentDownload';

	export default {
		name: 'OpportunityPreview',
		mixins: [ResourcePreview],
		components: {
            AttachmentDownload,
			UserCard,
			LayoutBase,
			SvgIcon,
		},
		data() {
			return {
				resource: {
					team: [],
				},
			}
		},
		filters: {
			formatUrgency: function(value) {
				if(value === null) {
					return '';
				}

				return (value) ? 'Urgent' : 'Normal';
			},
			formatMandated: function(value) {
				if(value === null) {
					return '';
				}

				return (value) ? 'Mandated' : 'Non-Mandated';
			},
		},
		methods: {
			stepLink(step) {
				return { name: 'edit-opportunity-step', params: { step: step, opportunity_id: this.id }}
			},
			fetchResource() {
				return API.getOpportunity(this.id);
			},
			updateResource(status) {
				return API.updateOpportunity(this.id, null, {status: status}).then(() => {
					this.$router.push({
						name: 'view-opportunity',
						params: {opportunity_id: this.id},
					});
				});
			},
			deleteResource() {
				return API.deleteOpportunity(this.id).then(() => {
					this.$router.push({ name: 'opportunities' });
				});
			},
			filterLead(users) {
				return users.filter(user => {
					return user.id !== this.resource.lead.id;
				});
			},
		}
	}
</script>

<style lang="scss" scoped>
	@import '~@/theme/_variables.scss';

	.unstyled{
		padding-left: 0;
		list-style: none;

		li {
			a{
				display: block;

				.svg-icon{
					width: 1.6rem;
					height: 1.6rem;
					margin-right: 0.5rem;
				}
			}
			+ li{
				margin-top: 0.6rem;
			}
		}
	}
</style>
